import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { Dialog } from '../../components/Dialog';
import { fmtDate } from '../../lib/format';
import { eurC } from '../../lib/money';
import { ChipRadios } from '../ChipRadios';
import { empfehlung, isHourly } from '../empfehlung';
import { deQty, dePrice, emptyRow, kundeWerteOf, parsePrice, ratesFor, summen } from '../formular';
import { useLeistungen, type Leistung } from '../LeistungDaten';
import type { EntwurfDaten } from '../RechnungForm';
import { nettoCents, zeitraum, type PositionsArt } from '../stundenPositionen';
import { fehlerText, useSchmal } from '../tarifHinweis';
import type { Kunde, Overview } from '../types';
import { einheit, waehrungAus } from '../waehrung';
import { stundenText } from './dauer';
import {
  eintraegeText,
  istOffen,
  leistungFuer,
  satzFuer,
  summeMinuten,
  zeitenEntwurfUrl,
  zeitenPositionen,
  zeitenText,
  zeitenUrl,
  type Zeit,
  type ZeitListe,
} from './ZeitDaten';

// „Rechnung aus Zeiten“ (ein Weg in die Rechnung): 1. Kunde mit offenen Zeiten wählen, 2. Einträge abhaken, Stundensatz
// (Vorschlag aus der letzten Rechnung an den Kunden, sonst aus einer Leistung in Std., sonst aus der letzten Rechnung
// mit Std.), Aufteilung und Vorschau, 3. Entwurf mit Vermerk „aus Zeiten“ anlegen (der Server prüft die Zeiten und
// setzt die IDs) und im Rechnungsformular weiterschreiben. Reserviert wird nichts: Verbunden werden die Zeiten erst beim
// Schreiben der Rechnung.

const ARTEN: [PositionsArt, string][] = [
  ['taetigkeit', 'Je Tätigkeit'],
  ['einzeln', 'Je Eintrag'],
  ['summe', 'Eine Position'],
];
// je Rechnung höchstens so viele Positionen (wie der Server, schemas.ts)
const POSITIONEN_MAX = 50;
// so viele Zeiten in eine Rechnung (wie der Server)
const ZEITEN_MAX = 500;
const ALLE = '';
const satzOk = (t: string) => {
  const p = parsePrice(t);
  return typeof p === 'number' && p > 0;
};
const hours = (n: number) => n.toLocaleString('de-DE', { maximumFractionDigits: 2 });

interface KundeOffen {
  kunde: NonNullable<Zeit['kunde']>;
  minuten: number;
  anzahl: number;
}

export function AbrechnenDialog({
  open,
  data,
  start,
  alle,
  onClose,
  onOhneKunde,
}: {
  open: boolean;
  data: Overview;
  // Kunde gleich gewählt (?abrechnen=<id>)
  start: string | null;
  // sieht alle Zeiten der Firma (Namen auf der Rechnung möglich)
  alle: boolean;
  onClose(): void;
  // „Zeiten ohne Kunde“: Liste dahin filtern
  onOhneKunde(): void;
}) {
  const navigate = useNavigate();
  const schmal = useSchmal();
  const leistungen = useLeistungen(data.firm.id);
  const [liste, setListe] = useState<ZeitListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kundeId, setKundeId] = useState<string | null>(null);
  const [objekt, setObjekt] = useState(ALLE);
  const [ohne, setOhne] = useState<Set<string>>(new Set());
  const [satz, setSatz] = useState('');
  const [satzGetippt, setSatzGetippt] = useState(false);
  const [art, setArt] = useState<PositionsArt>('taetigkeit');
  const [namen, setNamen] = useState(false);
  const [busy, setBusy] = useState(false);

  // beim Öffnen: alle offenen Zeiten in der Sicht (neueste 2.000)
  useEffect(() => {
    if (!open) return;
    let live = true;
    setListe(null);
    setFehler(null);
    setKundeId(start);
    setObjekt(ALLE);
    setOhne(new Set());
    setSatzGetippt(false);
    setArt('taetigkeit');
    setNamen(false);
    setBusy(false);
    api
      .get<ZeitListe>(zeitenUrl(data.firm.id, { monat: 'alle', status: 'offen' }))
      .then((l) => live && setListe(l))
      .catch((err) => live && setFehler(fehlerText(err)));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const offen = useMemo(() => (liste?.zeiten ?? []).filter(istOffen), [liste]);
  const ohneKunde = offen.filter((z) => !z.kunde).length;
  const kunden = useMemo(() => {
    const m = new Map<string, KundeOffen>();
    for (const z of offen) {
      if (!z.kunde) continue;
      const k = m.get(z.kunde.id) ?? { kunde: z.kunde, minuten: 0, anzahl: 0 };
      k.minuten += z.minuten;
      k.anzahl++;
      m.set(z.kunde.id, k);
    }
    return [...m.values()].sort((a, b) => b.minuten - a.minuten || a.kunde.name.localeCompare(b.kunde.name, 'de'));
  }, [offen]);

  // Kunde aus der Kundenliste (Anschrift, Währung, Zahlungsziel)
  const k: Kunde | null = kundeId ? (data.kunden.find((x) => x.id === kundeId) ?? null) : null;
  const w = waehrungAus(k?.waehrung);
  const euro = w === 'EUR';
  // Einträge des Kunden, ältester oben; Einsatzorte darin
  const eintraege = useMemo(
    () => offen.filter((z) => z.kunde?.id === kundeId).sort((a, b) => a.datum.localeCompare(b.datum) || a.id.localeCompare(b.id)),
    [offen, kundeId],
  );
  const objekte = useMemo(() => [...new Set(eintraege.map((z) => z.objekt).filter((o): o is string => !!o))].sort((a, b) => a.localeCompare(b, 'de')), [eintraege]);
  const mehrereOrte = objekte.length + (eintraege.some((z) => !z.objekt) ? 1 : 0) > 1;
  const sichtbar = objekt === ALLE ? eintraege : eintraege.filter((z) => z.objekt === objekt);
  const gewaehlt = sichtbar.filter((z) => !ohne.has(z.id));
  const minuten = summeMinuten(gewaehlt);
  // Einsatzort der Rechnung: das gewählte Objekt, oder das eine, wenn alle Einträge dasselbe haben
  const site = objekt !== ALLE ? objekt : !mehrereOrte && objekte.length === 1 ? objekte[0]! : '';

  // Vorschlag für den Stundensatz in der Währung des Kunden
  const stundenLeistung = useMemo(() => leistungen.list.find((l) => isHourly(l.unit) && l.price != null) ?? null, [leistungen.list]);
  const vorschlag = useMemo(() => {
    if (!k) return null;
    const verlauf = data.verlauf.filter((v) => (v.waehrung ?? 'EUR') === w);
    const emp = empfehlung(verlauf, { name: k.name, street: k.street, site });
    const ei = emp?.from.items.find((i) => isHourly(i.unit));
    if (emp && ei) return { price: dePrice(ei.price), text: `aus Ihrer Rechnung ${emp.from.number} vom ${fmtDate(emp.from.invoiceDate)}` };
    if (euro && stundenLeistung) return { price: dePrice(stundenLeistung.price!), text: `aus Ihrer Leistung „${stundenLeistung.name}“` };
    for (const v of verlauf) {
      const i = v.items.find((x) => isHourly(x.unit));
      if (i) return { price: dePrice(i.price), text: `aus Ihrer Rechnung ${v.number} vom ${fmtDate(v.invoiceDate)}` };
    }
    return null;
  }, [k, w, euro, site, data.verlauf, stundenLeistung]);
  useEffect(() => {
    if (!satzGetippt) setSatz(vorschlag?.price ?? '');
  }, [vorschlag, satzGetippt]);

  const zuViele = gewaehlt.length > POSITIONEN_MAX;
  const arten = zuViele ? ARTEN.filter(([a]) => a !== 'einzeln') : ARTEN;
  const artJetzt: PositionsArt = zuViele && art === 'einzeln' ? 'taetigkeit' : art;
  const mitNamen = namen && alle && artJetzt === 'einzeln';
  const positionen =
    satzOk(satz) && gewaehlt.length
      ? zeitenPositionen(gewaehlt, {
          art: artJetzt,
          preis: dePrice(parsePrice(satz) as number),
          namen: mitNamen,
          mehrereObjekte: objekt === ALLE && mehrereOrte,
          leistungen: leistungen.list,
          euro,
        })
      : [];
  const netto = positionen.length ? nettoCents(positionen) : null;
  // Leistungen, deren Preis die Vorschau nimmt
  const ausLeistung: Leistung[] =
    euro && artJetzt !== 'summe'
      ? [...new Map(gewaehlt.map((z) => leistungFuer(leistungen.list, z.taetigkeit)).filter((l): l is Leistung => !!l).map((l) => [l.id, l])).values()]
      : [];

  // Formularstand des Entwurfs wie beim Speichern im Rechnungsformular (IDs der Zeiten setzt der Server)
  function entwurf() {
    const kunde = kundeWerteOf(k!);
    const ku = data.absender.kleinunternehmer;
    // Steuersatz je Zeile: der Leistung, deren Preis sie hat, sonst der Stunden-Leistung (sonst 19 %) wie beim Stapel der
    // Stundenzettel
    const saetze = ratesFor(ku);
    const rows = positionen.map((p) => ({
      description: p.description,
      quantity: deQty(p.quantity),
      unit: p.unit,
      price: p.price,
      rate: satzFuer(p, stundenLeistung?.rate, saetze),
      discount: '',
    }));
    const { from, to } = zeitraum(gewaehlt.map((z) => ({ datum: z.datum, name: null, taetigkeit: null, stunden: 0 })));
    const daten: EntwurfDaten = {
      v: 1,
      art: 'normal',
      auftrag: '',
      picked: [],
      kunde,
      xrechnung: !!k!.xrechnung || kunde.art === 'behoerde',
      rows,
      serviceFrom: from ?? data.today,
      serviceTo: to && to !== from ? to : '',
      paymentDays: String(k!.paymentDays ?? data.absender.paymentDays),
      skonto: null,
      note: '',
      site,
      hinweis: `Aus ${zeitenText(gewaehlt.length)} (${stundenText(minuten)}). Bitte Stunden und Stundensatz prüfen.`,
      angebotId: null,
      waehrung: w,
      kurs: null,
      kursDatum: null,
    };
    const brutto = summen(
      rows.map((x) => ({ ...emptyRow(), ...x })),
      ku,
    ).gross;
    return { art: 'rechnung', titel: kunde.name.trim().slice(0, 200), betrag: brutto / 100, daten };
  }

  async function weiter() {
    if (!k || !gewaehlt.length || !positionen.length) return;
    setBusy(true);
    setFehler(null);
    try {
      const r = await api.post<{ id: string }>(zeitenEntwurfUrl(data.firm.id), { zeitIds: gewaehlt.map((z) => z.id), entwurf: entwurf() });
      onClose();
      navigate(`/rechnung/neu?entwurf=${r.id}`);
    } catch (err) {
      setFehler(fehlerText(err));
      setBusy(false);
    }
  }

  const alleAn = (an: boolean) => setOhne(an ? new Set() : new Set(sichtbar.map((z) => z.id)));
  const kundeName = k?.name ?? eintraege[0]?.kunde?.name ?? 'Kunde';

  return (
    <Dialog open={open} onClose={onClose} title="Rechnung aus Zeiten" className="zt-dlg zt-ab">
      {!liste ? (
        fehler ? <div className="notice bad">{fehler}</div> : <div className="spinner" aria-label="Lädt" />
      ) : !kundeId || (!eintraege.length && !k) ? (
        <>
          <p className="muted">Für welchen Kunden?</p>
          {kunden.length === 0 ? (
            <p className="notice info">Keine offenen Zeiten mit Kunde.</p>
          ) : (
            <ul className="zt-kunden">
              {kunden.map((x) => (
                <li key={x.kunde.id}>
                  <button type="button" className="zt-kunde-wahl" onClick={() => setKundeId(x.kunde.id)}>
                    <b>{x.kunde.name}</b>
                    <span className="muted">
                      {stundenText(x.minuten)} · {eintraegeText(x.anzahl)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {ohneKunde > 0 && (
            <p className="zt-ohne">
              <button type="button" className="linkbtn" onClick={onOhneKunde}>
                {zeitenText(ohneKunde)} ohne Kunde
              </button>{' '}
              – bitte zuerst einen Kunden eintragen
            </p>
          )}
          {liste.mehr && <p className="rt-hint zt-klein">Es sind mehr als 2.000 offene Zeiten. Gezeigt werden die neuesten.</p>}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="zt-ab-kunde">
            <b>{kundeName}</b>{' '}
            <button type="button" className="linkbtn" onClick={() => setKundeId(null)}>
              anderer Kunde
            </button>
          </p>
          {!eintraege.length ? (
            <p className="notice info">Für diesen Kunden gibt es keine offenen Zeiten.</p>
          ) : (
            <>
              {mehrereOrte && (
                <label className="field">
                  <span>Einsatzort</span>
                  <select
                    value={objekt}
                    onChange={(e) => {
                      setObjekt(e.target.value);
                      setOhne(new Set());
                    }}
                  >
                    <option value={ALLE}>Alle Einsatzorte</option>
                    {objekte.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="zt-ab-kopf">
                <span className="muted">
                  {gewaehlt.length} von {sichtbar.length} gewählt · {stundenText(minuten)}
                </span>
                <span className="zt-ab-alle">
                  <button type="button" className="linkbtn" onClick={() => alleAn(true)}>
                    Alle
                  </button>
                  <button type="button" className="linkbtn" onClick={() => alleAn(false)}>
                    Keine
                  </button>
                </span>
              </div>
              <ul className="zt-ab-list">
                {sichtbar.map((z) => (
                  <li key={z.id}>
                    <label className="check zt-ab-zeile">
                      <input
                        type="checkbox"
                        checked={!ohne.has(z.id)}
                        onChange={(e) =>
                          setOhne((cur) => {
                            const n = new Set(cur);
                            if (e.target.checked) n.delete(z.id);
                            else n.add(z.id);
                            return n;
                          })
                        }
                      />
                      <span className="zt-ab-t">
                        <span>
                          <b className="num">{fmtDate(z.datum)}</b> · <b className="num">{stundenText(z.minuten)}</b>
                        </span>
                        <small className="muted">{[z.taetigkeit, objekt === ALLE ? z.objekt : null, alle ? z.person?.name : null].filter(Boolean).join(' · ')}</small>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {gewaehlt.length > ZEITEN_MAX && <p className="rt-warn-line">Höchstens 500 Zeiten in eine Rechnung.</p>}

              <label className="field zt-satz">
                <span>Stundensatz netto (für Tätigkeiten ohne eigenen Preis)</span>
                <span className="zt-eur">
                  <input
                    value={satz}
                    inputMode="decimal"
                    placeholder="z. B. 45,00"
                    aria-invalid={!!satz.trim() && !satzOk(satz)}
                    onChange={(e) => {
                      setSatz(e.target.value);
                      setSatzGetippt(true);
                    }}
                  />
                  {einheit(w)} je Std.
                </span>
                {vorschlag && satz === vorschlag.price && <small>Vorschlag {vorschlag.text}</small>}
              </label>
              <div className="field">
                <span>Aufteilung</span>
                <ChipRadios<PositionsArt> label="Aufteilung" value={artJetzt} options={arten} onChange={setArt} />
                {zuViele && <small>„Je Eintrag“ geht nicht: höchstens 50 Positionen je Rechnung.</small>}
              </div>
              {artJetzt === 'einzeln' && alle && (
                <label className="check">
                  <input type="checkbox" checked={namen} onChange={(e) => setNamen(e.target.checked)} />
                  <span>
                    Namen auf die Rechnung
                    <br />
                    <small className="muted">Nur, wenn Ihr Kunde das verlangt.</small>
                  </span>
                </label>
              )}

              {positionen.length > 0 && (
                <details className="zt-vorschau" open={!schmal}>
                  <summary>
                    Vorschau: {positionen.length === 1 ? '1 Position' : `${positionen.length} Positionen`}
                    {netto != null && (
                      <>
                        {' '}
                        · <b className="num">{eurC(netto / 100, w)}</b> netto
                      </>
                    )}
                  </summary>
                  <ul>
                    {positionen.map((p, i) => (
                      <li key={i}>
                        <span>{p.description}</span>
                        <span className="num">
                          {hours(p.quantity)} Std. × {p.price} {einheit(w)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  {ausLeistung.map((l) => (
                    <p key={l.id} className="rt-hint zt-klein">
                      Preis aus der Leistung „{l.name}“
                    </p>
                  ))}
                </details>
              )}
            </>
          )}
          {fehler && <div className="notice bad">{fehler}</div>}
          <div className="dlg-actions zt-actions">
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={busy || !k || !gewaehlt.length || !positionen.length || gewaehlt.length > ZEITEN_MAX}
              onClick={() => void weiter()}
            >
              Weiter zur Rechnung ({zeitenText(gewaehlt.length)}, {stundenText(minuten)})
            </button>
          </div>
          {!satzOk(satz) && gewaehlt.length > 0 && <p className="rt-warn-line">Bitte den Stundensatz eintragen.</p>}
        </>
      )}
    </Dialog>
  );
}
